/**
 * Typed FastAPI client for TMA.
 * Lightweight fetch wrapper with Zod runtime validation on response.
 *
 * Auth: the self-profile route is gated by a session JWT, NOT raw initData. We
 * bootstrap once per session — `initData → POST /auth/telegram → access_token` —
 * then send `Authorization: Bearer <access_token>` on the gated call
 * (`ensureSessionToken` + `GET /users/me`). The chart/transit compute endpoints
 * are PUBLIC (stateless POSTs) — they take birth data in the body, so we read
 * the user's birth data from `GET /users/me` and forward it.
 *
 * Default is the REAL API → api.astrovelia.com. Set `VITE_MOCK_API=1` for a
 * network-free dev/preview run on local fixtures.
 */
import { z } from "zod";

import { fetchLunarToday } from "@/lib/lunar-api";

interface ViteEnvShape {
  readonly VITE_API_BASE?: string;
  readonly VITE_MOCK_API?: string;
}

/**
 * Read the build-time `VITE_*` variables.
 *
 * `import.meta.env` MUST be written out in full here. Vite replaces that exact
 * token textually at build time; assigning `import.meta` to a local first and
 * reading `.env` off the local defeats the replacement, and native
 * `import.meta` has no `env` — so every variable silently read as `undefined`
 * in the browser while still working under vitest (which provides a real
 * `import.meta.env`). That is exactly how `VITE_API_BASE` and `VITE_MOCK_API`
 * went dead without a single failing test: dev and prod both talked to the
 * production API regardless of configuration.
 *
 * The `try` still guards non-Vite runtimes where `import.meta.env` is absent.
 */
function readEnv(): ViteEnvShape {
  try {
    return (import.meta.env ?? {}) as ViteEnvShape;
  } catch {
    return {};
  }
}

const env = readEnv();
export const API_BASE = env.VITE_API_BASE ?? "https://api.astrovelia.com";
/**
 * Real API is the default. Mock fixtures are opt-in for dev/preview via
 * `VITE_MOCK_API=1` (no network, no auth) — every other value (including unset)
 * hits the live backend.
 */
export const MOCK_MODE = env.VITE_MOCK_API === "1";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

interface FetchOpts<T> {
  readonly path: string;
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  /** Session JWT (from `ensureSessionToken`). Sent as `Authorization: Bearer`. */
  readonly token?: string | null;
  readonly schema: z.ZodType<T>;
}

export async function apiFetch<T>(opts: FetchOpts<T>): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (opts.token) {
    headers.Authorization = `Bearer ${opts.token}`;
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(`${API_BASE}${opts.path}`, init);
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    throw new ApiError(`API ${opts.method ?? "GET"} ${opts.path} failed`, res.status, parsed);
  }
  return opts.schema.parse(parsed);
}

/* ----- Session bootstrap (initData → session JWT) ----- */

/**
 * Subset of FastAPI's `SessionResponse` (services/api/.../schemas/auth.py) we use.
 * The session JWT lives in `access_token`; the rest of the envelope is ignored.
 */
const sessionResponseSchema = z.object({
  access_token: z.string().min(1),
});

/** In-memory cache so we exchange initData for a JWT at most once per app load. */
let sessionTokenPromise: Promise<string> | null = null;

/**
 * Resolve a session JWT for the current Telegram launch.
 *
 * Exchanges `initData` at `POST /auth/telegram` (body `{ init_data }`, matching
 * `TelegramVerifyRequest`) and caches the resulting `access_token` for the app's
 * lifetime. Throws when there is no `initData` (running outside Telegram) — the
 * self-profile route is unreachable without a session.
 */
export async function ensureSessionToken(initData: string | null): Promise<string> {
  if (!initData) {
    throw new ApiError("No Telegram initData — cannot establish a session", 401, null);
  }
  if (sessionTokenPromise === null) {
    sessionTokenPromise = apiFetch({
      path: "/auth/telegram",
      method: "POST",
      body: { init_data: initData },
      schema: sessionResponseSchema,
    })
      .then((s) => s.access_token)
      .catch((err: unknown) => {
        // Don't cache a failed exchange — let the next call retry.
        sessionTokenPromise = null;
        throw err;
      });
  }
  return sessionTokenPromise;
}

/** Test-only: drop the cached session so each test starts clean. */
export function __resetSessionToken(): void {
  sessionTokenPromise = null;
}

/* ----- Self profile (GET /users/me) ----- */

/**
 * Subset of FastAPI's `MeProfileResponse` (services/api/.../schemas/beta.py).
 * Birth fields are nullable: a session that authenticated but hasn't onboarded
 * has `onboarded=false` and null birth data — a valid, non-error state.
 */
export const meProfileResponseSchema = z.object({
  user_id: z.string().nullable(),
  onboarded: z.boolean(),
  subscription_tier: z.string(),
  first_name: z.string().nullable().optional(),
  birth_date: z.string().nullable().optional(), // YYYY-MM-DD
  birth_time: z.string().nullable().optional(), // HH:MM[:SS]
  birth_lat: z.number().nullable().optional(),
  birth_lon: z.number().nullable().optional(),
  timezone: z.string().nullable().optional(), // IANA
  birth_place: z.string().nullable().optional(),
});
export type MeProfileResponse = z.infer<typeof meProfileResponseSchema>;

/** Fetch the authenticated user's own profile (session-gated). */
export async function fetchMe(initData: string | null): Promise<MeProfileResponse> {
  const token = await ensureSessionToken(initData);
  return apiFetch({ path: "/users/me", token, schema: meProfileResponseSchema });
}

/**
 * Birth data ready to forward to the compute endpoints. Returned by
 * `requireBirthData` when the profile is onboarded with a full coordinate set;
 * `null` otherwise (caller shows the dignified "fill your profile" state).
 */
interface BirthData {
  readonly date: string;
  readonly time: string | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly timezone: string;
  readonly place: string | null;
}

/** Extract complete birth data from a profile, or null if not onboarded. */
function birthDataFromProfile(me: MeProfileResponse): BirthData | null {
  if (
    !me.onboarded ||
    !me.birth_date ||
    me.birth_lat === null ||
    me.birth_lat === undefined ||
    me.birth_lon === null ||
    me.birth_lon === undefined ||
    !me.timezone
  ) {
    return null;
  }
  return {
    date: me.birth_date,
    time: me.birth_time ?? null,
    latitude: me.birth_lat,
    longitude: me.birth_lon,
    timezone: me.timezone,
    place: me.birth_place ?? null,
  };
}

/* ----- Reference maps (RU labels + glyphs; mirror the lunar components) ----- */

/** English planet name (chart payload) → glyph. */
const PLANET_GLYPH: Readonly<Record<string, string>> = {
  Sun: "☉",
  Moon: "☽",
  Mercury: "☿",
  Venus: "♀",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
  Uranus: "♅",
  Neptune: "♆",
  Pluto: "♇",
  Chiron: "⚷",
  Lilith: "⚸",
  NorthNode: "☊",
  SouthNode: "☋",
};

/** English planet name → dignified RU display name. */
const PLANET_RU: Readonly<Record<string, string>> = {
  Sun: "Солнце",
  Moon: "Луна",
  Mercury: "Меркурий",
  Venus: "Венера",
  Mars: "Марс",
  Jupiter: "Юпитер",
  Saturn: "Сатурн",
  Uranus: "Уран",
  Neptune: "Нептун",
  Pluto: "Плутон",
  Chiron: "Хирон",
  Lilith: "Лилит",
  NorthNode: "Северный узел",
  SouthNode: "Южный узел",
};

/** English sign name (chart payload) → glyph. */
const SIGN_GLYPH: Readonly<Record<string, string>> = {
  Aries: "♈",
  Taurus: "♉",
  Gemini: "♊",
  Cancer: "♋",
  Leo: "♌",
  Virgo: "♍",
  Libra: "♎",
  Scorpio: "♏",
  Sagittarius: "♐",
  Capricorn: "♑",
  Aquarius: "♒",
  Pisces: "♓",
};

/** Aspect Literal → glyph used in transit/aspect titles. */
const ASPECT_GLYPH: Readonly<Record<AspectKind, string>> = {
  conjunction: "☌",
  sextile: "⚹",
  square: "□",
  trine: "△",
  opposition: "☍",
};

/** Aspect Literal → instrumental-case RU label for aspect detail prose. */
const ASPECT_RU: Readonly<Record<AspectKind, string>> = {
  conjunction: "Соединение",
  sextile: "Секстиль",
  square: "Квадратура",
  trine: "Тригон",
  opposition: "Оппозиция",
};

/** Planet glyph for a known body, else a neutral dot (never fabricate). */
function planetGlyph(name: string): string {
  return PLANET_GLYPH[name] ?? "•";
}

/** Dignified RU planet name for a known body, else the raw engine name. */
function planetRu(name: string): string {
  return PLANET_RU[name] ?? name;
}

/** Stable lowercase key (Sun→sun) so ExplanationSheet resolves its meanings. */
function planetKey(name: string): string {
  return name.toLowerCase();
}

/** Split a non-negative degree value into whole degrees + minutes (handles the
 *  59.6′→60′ rounding rollover so we never print "3°60′"). */
function degMin(value: number): { readonly deg: number; readonly min: number } {
  const abs = Math.abs(value);
  let deg = Math.floor(abs);
  let min = Math.round((abs - deg) * 60);
  if (min === 60) {
    deg += 1;
    min = 0;
  }
  return { deg, min };
}

/**
 * "12°45′♏" from an ecliptic longitude + sign. Degrees are shown within the
 * sign (0–29), matching how the lunar position line is formatted.
 */
function formatLongitude(longitude: number, sign: string): string {
  const { deg, min } = degMin(((longitude % 30) + 30) % 30);
  const glyph = SIGN_GLYPH[sign] ?? "";
  return `${deg}°${min.toString().padStart(2, "0")}′${glyph}`;
}

/** "3°56′" orb formatting (degrees floored, minutes rounded with rollover). */
function formatOrb(orb: number): string {
  const { deg, min } = degMin(orb);
  return `${deg}°${min.toString().padStart(2, "0")}′`;
}

/* ----- Display schemas (what the screens render) ----- */

export const transitSchema = z.object({
  id: z.string(),
  icon: z.string(),
  title: z.string(),
  time: z.string(),
  aspect: z.enum(["trine", "sextile", "square", "opposition", "conjunction"]),
});
export type Transit = z.infer<typeof transitSchema>;
type AspectKind = Transit["aspect"];

/**
 * Today dashboard, assembled client-side from real endpoints:
 *   - `moonPhase` ← GET /lunar/today
 *   - `transits`  ← POST /transits (significant aspects)
 * `onboarded=false` is the dignified empty state (no birth data on file).
 * `streak` is mock-only — there is no backend for it, so it is omitted in real
 * mode rather than fabricated (data-correctness).
 */
export const todaySchema = z.object({
  onboarded: z.boolean(),
  date: z.string(),
  greeting: z.string(),
  moonPhase: z.object({
    label: z.string(),
    position: z.string(),
    headline: z.string(),
    body: z.string(),
  }),
  transits: z.array(transitSchema),
  streak: z
    .object({
      days: z.number().int().nonnegative(),
      target: z.number().int().positive(),
      todayChecked: z.boolean(),
    })
    .optional(),
});
export type TodayResponse = z.infer<typeof todaySchema>;

export const planetSchema = z.object({
  key: z.string(),
  glyph: z.string(),
  name: z.string(),
  degree: z.string(),
  /**
   * Absolute ecliptic longitude, 0–360. Carried through from the engine
   * (unrounded) because the natal wheel plots from it — the pretty `degree`
   * string is lossy (it is degrees WITHIN the sign).
   */
  longitude: z.number(),
  /** House number 1–12 from the engine's house system. */
  house: z.number().int(),
  retrograde: z.boolean(),
});
export type Planet = z.infer<typeof planetSchema>;

export const aspectSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string(),
  kind: z.enum(["trine", "sextile", "square", "opposition", "conjunction"]),
  /** Lowercase planet keys of the two ends, so the wheel can draw the chord. */
  from: z.string(),
  to: z.string(),
});
export type AspectSummary = z.infer<typeof aspectSummarySchema>;

/**
 * The geometry the natal wheel needs beyond the planet list: where the chart
 * is rotated to (ascendant) and where the house cusps fall. All values are
 * absolute ecliptic longitudes in degrees.
 */
export const wheelGeometrySchema = z.object({
  ascendant: z.number(),
  midheaven: z.number(),
  /** 12 cusp longitudes, house 1 first. */
  houses: z.array(z.number()),
});
export type WheelGeometry = z.infer<typeof wheelGeometrySchema>;

export const chartSchema = z.object({
  meta: z.string(),
  planets: z.array(planetSchema),
  aspects: z.array(aspectSummarySchema),
  wheel: wheelGeometrySchema,
});
export type ChartResponse = z.infer<typeof chartSchema>;

/* ----- Week transit feed (POST /transits/week) ----- */

export const transitDaySchema = z.object({
  /** YYYY-MM-DD of the sample. */
  date: z.string(),
  /** "Сегодня · 19 августа" / "Четверг · 21 августа". */
  label: z.string(),
  transits: z.array(transitSchema),
});
export type TransitDay = z.infer<typeof transitDaySchema>;

export const transitsWeekSchema = z.object({
  onboarded: z.boolean(),
  /** "19–25 августа". */
  rangeLabel: z.string(),
  days: z.array(transitDaySchema),
});
export type TransitsWeekResponse = z.infer<typeof transitsWeekSchema>;

export const userCreateRequestSchema = z.object({
  name: z.string().min(1).max(64),
  birthDate: z.string(), // YYYY-MM-DD
  birthTime: z.string().nullable(), // HH:MM or null when unknown
  birthPlace: z.string().min(1).max(120),
  timezone: z.string().nullable(),
  // Coordinates from the picked city. Optional in the type so callers that
  // haven't wired city lat/lon yet still compile; REQUIRED at runtime in real
  // mode because PATCH /users/me persists NOT-NULL strict-float columns and we
  // refuse to fabricate a birthplace (data-correctness).
  birthLat: z.number().min(-90).max(90).optional(),
  birthLon: z.number().min(-180).max(180).optional(),
});
export type UserCreateRequest = z.infer<typeof userCreateRequestSchema>;

/**
 * Maps the camelCase onboarding payload onto FastAPI's `MeProfileUpdateRequest`
 * (snake_case, services/api/.../schemas/beta.py). `birth_time: null` is the
 * documented solar-fallback. Validated by the server (extra="forbid").
 */
export const meProfileUpdateSchema = z.object({
  first_name: z.string().min(1).max(100),
  birth_date: z.string(),
  birth_time: z.string().nullable(),
  birth_lat: z.number().min(-90).max(90),
  birth_lon: z.number().min(-180).max(180),
  timezone: z.string().min(1).max(64),
  birth_place: z.string().min(1).max(128),
});
export type MeProfileUpdate = z.infer<typeof meProfileUpdateSchema>;

/** One hit from FastAPI's `AtlasSearchResponse.results` (services/api/.../schemas/atlas.py). */
const cityHitSchema = z.object({
  name: z.string(),
  country_code: z.string(),
  lat: z.number(),
  lon: z.number(),
  timezone_iana: z.string(),
});

/** `GET /atlas/search` envelope. */
const atlasSearchSchema = z.object({
  results: z.array(cityHitSchema),
});

export const citySchema = z.object({
  id: z.string(),
  name: z.string(),
  country: z.string(),
  timezone: z.string(),
  lat: z.number(),
  lon: z.number(),
});
export type City = z.infer<typeof citySchema>;

/* ----- Real wire schemas (mirror the FastAPI response models) ----- */

/** Subset of `ChartResponse` (services/api/.../schemas/chart.py) the TMA uses. */
const wireChartSchema = z.object({
  planets: z.array(
    z.object({
      name: z.string(),
      longitude: z.number(),
      sign: z.string(),
      house: z.number().int(),
      retrograde: z.boolean(),
    }),
  ),
  aspects: z.array(
    z.object({
      planet1: z.string(),
      planet2: z.string(),
      type: z.enum(["conjunction", "sextile", "square", "trine", "opposition"]),
      orb: z.number(),
    }),
  ),
  // Wheel geometry. Optional so a backend that predates these fields still
  // parses — the wheel falls back to an Aries-rising orientation and hides the
  // house spokes rather than drawing invented cusps.
  ascendant: z.number().optional(),
  midheaven: z.number().optional(),
  houses: z.array(z.number()).optional(),
});

/**
 * Subset of `TransitChartResponse` (services/api/.../schemas/transit.py). Decimal
 * fields serialise as JSON strings (Pydantic), and `aspect` is null for the bare
 * transiting-body rows; we coerce + filter in the mapper. `planet` carries the
 * "Transiting->Natal" pair for aspect rows.
 */
const wireTransitSchema = z.object({
  transits: z.array(
    z.object({
      planet: z.string(),
      orb: z.coerce.number(),
      aspect: z.enum(["conjunction", "sextile", "square", "trine", "opposition"]).nullable(),
      is_applying: z.boolean(),
    }),
  ),
});

/**
 * Subset of `TransitForecastResponse` (services/api/.../schemas/transit.py) as
 * returned by `POST /transits/week`: 7 daily samples of the same row shape the
 * one-shot `/transits` endpoint uses.
 */
const wireTransitsWeekSchema = z.object({
  samples: z.array(
    z.object({
      transit_date: z.string(),
      transits: z.array(
        z.object({
          planet: z.string(),
          orb: z.coerce.number(),
          aspect: z.enum(["conjunction", "sextile", "square", "trine", "opposition"]).nullable(),
          is_applying: z.boolean(),
        }),
      ),
    }),
  ),
});

/**
 * Rows shown per day in the week feed. The engine returns ~10-15 aspect rows a
 * day; past the tightest handful they are wide-orb background noise, and a
 * phone screen full of them reads as a data dump rather than a forecast.
 */
const MAX_TRANSITS_PER_DAY = 5;

/* ----- Server → display mappers ----- */

/** Map a real `ChartResponse` + birth meta onto the screen's `ChartResponse`. */
function toChartResponse(wire: z.infer<typeof wireChartSchema>, meta: string): ChartResponse {
  const planets: Planet[] = wire.planets.map((p) => ({
    key: planetKey(p.name),
    glyph: planetGlyph(p.name),
    name: planetRu(p.name),
    degree: formatLongitude(p.longitude, p.sign),
    longitude: p.longitude,
    house: p.house,
    retrograde: p.retrograde,
  }));
  const aspects: AspectSummary[] = wire.aspects.map((a, i) => {
    const p1 = planetRu(a.planet1);
    const p2 = planetRu(a.planet2);
    return {
      id: `${a.planet1}-${a.planet2}-${a.type}-${i}`,
      title: `${p1} ${ASPECT_GLYPH[a.type]} ${p2}`,
      detail: `${ASPECT_RU[a.type]}, орб ${formatOrb(a.orb)}.`,
      kind: a.type,
      from: planetKey(a.planet1),
      to: planetKey(a.planet2),
    };
  });
  // Only pass through cusps the engine actually returned. A partial set would
  // draw house spokes in the wrong places, which is worse than none.
  const houses = wire.houses?.length === 12 ? wire.houses : [];
  return {
    meta,
    planets,
    aspects,
    wheel: {
      ascendant: wire.ascendant ?? 0,
      midheaven: wire.midheaven ?? 0,
      houses,
    },
  };
}

/** Build the chart-head meta line from birth data ("14.03.1992 · 19:30 · Москва"). */
function metaFromBirth(b: BirthData): string {
  const parts = b.date.split("-");
  const dmy = parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : b.date;
  const time = b.time ? b.time.slice(0, 5) : "время неизвестно";
  return [dmy, time, b.place].filter(Boolean).join(" · ");
}

/** One significant transit row → display `Transit`. */
function toTransit(
  row: z.infer<typeof wireTransitSchema>["transits"][number],
  i: number,
): Transit | null {
  if (row.aspect === null) return null;
  // Aspect rows carry "Transiting->Natal"; bare rows carry a single planet name.
  const [transiting, natal] = row.planet.split("->");
  const glyph = planetGlyph(transiting ?? row.planet);
  const lhs = planetRu(transiting ?? row.planet);
  const title = natal
    ? `${lhs} ${ASPECT_GLYPH[row.aspect]} ${planetRu(natal)}`
    : `${lhs} ${ASPECT_GLYPH[row.aspect]}`;
  const orb = `${row.orb.toFixed(1)}°`;
  const phase = row.is_applying ? "сходится" : "расходится";
  return {
    id: `${row.planet}-${row.aspect}-${i}`,
    icon: glyph,
    title,
    time: `орб ${orb} · ${phase}`,
    aspect: row.aspect,
  };
}

/* ----- Mock fixtures (VITE_MOCK_API=1) -----
   Match the display contract verbatim so a switch to real fetch is a flag flip. */

const MOCK_TODAY: TodayResponse = {
  onboarded: true,
  date: "2026-05-11",
  greeting: "Понедельник · 11 мая",
  moonPhase: {
    label: "Растущая Луна",
    position: "14°22′ ♍ · 3 дом",
    headline: "День ясности и ревизии планов.",
    body: "Луна в Деве проходит через ваш третий дом, образуя точный тригон к Сатурну. Удачный день, чтобы пересмотреть рабочие документы.",
  },
  transits: [
    {
      id: "t1",
      icon: "☿",
      title: "Меркурий тригон Сатурн",
      time: "12:47 · точный · 3 дн",
      aspect: "trine",
    },
    {
      id: "t2",
      icon: "♂",
      title: "Марс квадрат Луна",
      time: "18:22 · ослабевает",
      aspect: "square",
    },
    {
      id: "t3",
      icon: "♀",
      title: "Венера секстиль Юпитер",
      time: "21:08 · усиливается",
      aspect: "sextile",
    },
  ],
  streak: { days: 4, target: 7, todayChecked: true },
};

const MOCK_CHART: ChartResponse = {
  meta: "14.03.1992 · 04:17 · Москва",
  planets: [
    // `longitude` = sign index * 30 + the degrees shown in `degree`, so the
    // mock wheel is internally consistent with its own labels.
    {
      key: "sun",
      glyph: "☉",
      name: "Солнце",
      degree: "12°45′♏",
      longitude: 222.75,
      house: 5,
      retrograde: false,
    },
    {
      key: "moon",
      glyph: "☽",
      name: "Луна",
      degree: "23°12′♍",
      longitude: 173.2,
      house: 4,
      retrograde: false,
    },
    {
      key: "mercury",
      glyph: "☿",
      name: "Меркурий",
      degree: "05°22′♐",
      longitude: 245.37,
      house: 6,
      retrograde: false,
    },
    {
      key: "venus",
      glyph: "♀",
      name: "Венера",
      degree: "28°44′♎",
      longitude: 208.73,
      house: 5,
      retrograde: false,
    },
    {
      key: "mars",
      glyph: "♂",
      name: "Марс",
      degree: "18°01′♓",
      longitude: 348.02,
      house: 9,
      retrograde: false,
    },
    {
      key: "jupiter",
      glyph: "♃",
      name: "Юпитер",
      degree: "09°33′♍",
      longitude: 159.55,
      house: 4,
      retrograde: true,
    },
    {
      key: "saturn",
      glyph: "♄",
      name: "Сатурн",
      degree: "12°58′♒",
      longitude: 312.97,
      house: 8,
      retrograde: false,
    },
    {
      key: "uranus",
      glyph: "♅",
      name: "Уран",
      degree: "16°44′♑",
      longitude: 286.73,
      house: 7,
      retrograde: false,
    },
  ],
  aspects: [
    {
      id: "a1",
      title: "Солнце ☍ Луна",
      detail: "Оппозиция, орб 1°33′.",
      kind: "opposition",
      from: "sun",
      to: "moon",
    },
    {
      id: "a2",
      title: "Меркурий △ Сатурн",
      detail: "Тригон, орб 0°44′.",
      kind: "trine",
      from: "mercury",
      to: "saturn",
    },
    {
      id: "a3",
      title: "Венера ☌ Юпитер",
      detail: "Соединение, орб 2°10′.",
      kind: "conjunction",
      from: "venus",
      to: "jupiter",
    },
  ],
  wheel: {
    ascendant: 185.1,
    midheaven: 97.03,
    houses: [
      185.1, 208.63, 239.12, 277.03, 313.88, 342.95, 5.1, 28.63, 59.12, 97.03, 133.88, 162.95,
    ],
  },
};

/**
 * Mock week feed (VITE_MOCK_API=1). Built relative to `now` so the labels
 * ("Сегодня", weekday names) stay correct whenever the preview is opened —
 * a frozen fixture would show stale weekdays.
 */
function mockWeek(now: Date): TransitsWeekResponse {
  const rows: readonly (readonly Transit[])[] = [
    [
      {
        id: "w1",
        icon: "☿",
        title: "Меркурий △ Сатурн",
        time: "орб 0.4° · сходится",
        aspect: "trine",
      },
      {
        id: "w2",
        icon: "♂",
        title: "Марс □ Луна",
        time: "орб 1.2° · расходится",
        aspect: "square",
      },
    ],
    [
      {
        id: "w3",
        icon: "♀",
        title: "Венера ⚹ Юпитер",
        time: "орб 0.9° · сходится",
        aspect: "sextile",
      },
    ],
    [
      {
        id: "w4",
        icon: "☉",
        title: "Солнце ☌ Уран",
        time: "орб 1.8° · сходится",
        aspect: "conjunction",
      },
      {
        id: "w5",
        icon: "♄",
        title: "Сатурн ☍ Венера",
        time: "орб 2.1° · расходится",
        aspect: "opposition",
      },
    ],
  ];
  const days: TransitDay[] = Array.from({ length: 7 }, (_, i) => {
    const when = new Date(now);
    when.setDate(when.getDate() + i);
    return {
      date: localIsoDate(when),
      label: dayLabel(when, now),
      transits: [...(rows[i % rows.length] ?? [])].map((t) => ({ ...t, id: `${t.id}-${i}` })),
    };
  });
  const last = new Date(now);
  last.setDate(last.getDate() + 6);
  return {
    onboarded: true,
    rangeLabel: `${WEEK_RANGE_FMT.format(now)} — ${WEEK_RANGE_FMT.format(last)}`,
    days,
  };
}

const MOCK_CITIES: readonly City[] = [
  {
    id: "ru-msk",
    name: "Москва",
    country: "Россия",
    timezone: "Europe/Moscow",
    lat: 55.7558,
    lon: 37.6173,
  },
  {
    id: "ru-spb",
    name: "Санкт-Петербург",
    country: "Россия",
    timezone: "Europe/Moscow",
    lat: 59.9311,
    lon: 30.3609,
  },
  {
    id: "ru-ekb",
    name: "Екатеринбург",
    country: "Россия",
    timezone: "Asia/Yekaterinburg",
    lat: 56.8389,
    lon: 60.6057,
  },
  {
    id: "ru-nvs",
    name: "Новосибирск",
    country: "Россия",
    timezone: "Asia/Novosibirsk",
    lat: 55.0084,
    lon: 82.9357,
  },
  {
    id: "by-mns",
    name: "Минск",
    country: "Беларусь",
    timezone: "Europe/Minsk",
    lat: 53.9006,
    lon: 27.559,
  },
  {
    id: "kz-alm",
    name: "Алматы",
    country: "Казахстан",
    timezone: "Asia/Almaty",
    lat: 43.222,
    lon: 76.8512,
  },
  {
    id: "ua-kie",
    name: "Киев",
    country: "Украина",
    timezone: "Europe/Kiev",
    lat: 50.4501,
    lon: 30.5234,
  },
  {
    id: "us-nyc",
    name: "Нью-Йорк",
    country: "США",
    timezone: "America/New_York",
    lat: 40.7128,
    lon: -74.006,
  },
  {
    id: "gb-lon",
    name: "Лондон",
    country: "Великобритания",
    timezone: "Europe/London",
    lat: 51.5074,
    lon: -0.1278,
  },
  {
    id: "de-ber",
    name: "Берлин",
    country: "Германия",
    timezone: "Europe/Berlin",
    lat: 52.52,
    lon: 13.405,
  },
];

function delay<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/* ----- Typed endpoint helpers ----- */

const TODAY_DATE_FMT = new Intl.DateTimeFormat("ru-RU", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

/** Capitalise the first letter ("понедельник" → "Понедельник"). */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Assemble the Today dashboard from real endpoints.
 *
 * Real mode: session → GET /users/me. When onboarded, POST /transits (birth data
 * + now) and GET /lunar/today (device tz) run in parallel; the moon card comes
 * from real lunar data and the transit rows from significant aspects. When the
 * user has no birth data on file we return `onboarded: false` and the screen
 * shows a dignified "fill your profile" placeholder — never fabricated transits.
 */
export async function fetchToday(initData: string | null, tz: string): Promise<TodayResponse> {
  if (MOCK_MODE) return delay(MOCK_TODAY);

  const me = await fetchMe(initData);
  const birth = birthDataFromProfile(me);
  const now = new Date();
  const greeting = capitalize(TODAY_DATE_FMT.format(now));
  if (!birth) {
    return {
      onboarded: false,
      date: now.toISOString().slice(0, 10),
      greeting,
      moonPhase: { label: "", position: "", headline: "", body: "" },
      transits: [],
    };
  }

  const [transitWire, lunar] = await Promise.all([
    apiFetch({
      path: "/transits",
      method: "POST",
      body: {
        natal_birth: {
          date: birth.date,
          time: birth.time,
          latitude: birth.latitude,
          longitude: birth.longitude,
          timezone: birth.timezone,
        },
        transit_date: now.toISOString(),
      },
      schema: wireTransitSchema,
    }),
    // Reuse the shared lunar client (mock-aware, public route, no session).
    fetchLunarTodaySnapshot(tz),
  ]);

  // Same three rules as the week feed: keep only rows that carry an aspect AND
  // both ends of the pair (the engine also emits a bare same-planet row that
  // duplicates "X->X" and renders as a dangling "Сатурн ⚹" with nothing after
  // it), tightest orb first, capped so the card stays a briefing.
  const transits = transitWire.transits
    .filter((r) => r.aspect !== null && r.planet.includes("->"))
    .sort((a, b) => a.orb - b.orb)
    .slice(0, MAX_TRANSITS_PER_DAY)
    .map((row, i) => toTransit(row, i))
    .filter((t): t is Transit => t !== null);

  return {
    onboarded: true,
    date: now.toISOString().slice(0, 10),
    greeting,
    moonPhase: lunar,
    transits,
  };
}

/**
 * Compute the user's natal chart.
 *
 * Real mode: session → GET /users/me → POST /chart (public) with the stored
 * birth data and `include_extended`. Throws a dignified ApiError when the user
 * has no birth data on file (the screen shows the placeholder banner) — we never
 * invent a birthplace.
 */
export async function fetchChart(initData: string | null): Promise<ChartResponse> {
  if (MOCK_MODE) return delay(MOCK_CHART);

  const me = await fetchMe(initData);
  const birth = birthDataFromProfile(me);
  if (!birth) {
    throw new ApiError("Профиль не заполнен — добавьте данные рождения в кабинете", 404, null);
  }
  const wire = await apiFetch({
    path: "/chart",
    method: "POST",
    body: {
      birth_data: {
        date: birth.date,
        time: birth.time,
        latitude: birth.latitude,
        longitude: birth.longitude,
        timezone: birth.timezone,
      },
      include_extended: true,
    },
    schema: wireChartSchema,
  });
  return toChartResponse(wire, metaFromBirth(birth));
}

/* ----- Week transit feed ----- */

const WEEK_DAY_FMT = new Intl.DateTimeFormat("ru-RU", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const WEEK_RANGE_FMT = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });

/** Local YYYY-MM-DD (never `toISOString`, which silently shifts to UTC). */
function localIsoDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "Сегодня · 19 августа" for today, else "Четверг · 21 августа". */
function dayLabel(sample: Date, today: Date): string {
  // ru-RU formats as "четверг, 21 августа"; the section headers read better
  // with the middot the rest of the app uses.
  const formatted = capitalize(WEEK_DAY_FMT.format(sample)).replace(", ", " · ");
  if (localIsoDate(sample) !== localIsoDate(today)) return formatted;
  // Same shape, with the weekday word swapped for "Сегодня".
  const datePart = formatted.split(" · ").slice(1).join(" · ") || formatted;
  return `Сегодня · ${datePart}`;
}

/**
 * Fetch the 7-day transit feed, grouped by day.
 *
 * Real mode: session → GET /users/me → POST /transits/week (public) with the
 * stored birth data. Each daily sample keeps only rows that carry BOTH an
 * aspect and a "Transiting->Natal" pair; the engine also emits a bare
 * same-planet row that duplicates `X->X`, and rows with `aspect: null` which
 * carry no finding at all. Rows are ordered tightest-orb first and capped so a
 * day reads as a briefing rather than a dump.
 *
 * `onboarded: false` (no birth data on file) yields an empty feed — the screen
 * shows the same dignified prompt as Today rather than inventing transits.
 */
export async function fetchTransitsWeek(
  initData: string | null,
  now: Date = new Date(),
): Promise<TransitsWeekResponse> {
  if (MOCK_MODE) return delay(mockWeek(now));

  const me = await fetchMe(initData);
  const birth = birthDataFromProfile(me);
  if (!birth) {
    return { onboarded: false, rangeLabel: "", days: [] };
  }

  const wire = await apiFetch({
    path: "/transits/week",
    method: "POST",
    body: {
      natal_birth: {
        date: birth.date,
        time: birth.time,
        latitude: birth.latitude,
        longitude: birth.longitude,
        timezone: birth.timezone,
      },
      start: now.toISOString(),
    },
    schema: wireTransitsWeekSchema,
  });

  const days: TransitDay[] = wire.samples.map((sample) => {
    const when = new Date(sample.transit_date);
    const rows = sample.transits
      .filter((r) => r.aspect !== null && r.planet.includes("->"))
      .sort((a, b) => a.orb - b.orb)
      .slice(0, MAX_TRANSITS_PER_DAY);
    return {
      date: localIsoDate(when),
      label: dayLabel(when, now),
      transits: rows.map((row, i) => toTransit(row, i)).filter((t): t is Transit => t !== null),
    };
  });

  const first = wire.samples[0];
  const last = wire.samples[wire.samples.length - 1];
  const rangeLabel =
    first && last
      ? `${WEEK_RANGE_FMT.format(new Date(first.transit_date))} — ${WEEK_RANGE_FMT.format(new Date(last.transit_date))}`
      : "";

  return { onboarded: true, rangeLabel, days };
}

/**
 * Persist the user's birth data.
 *
 * Real mode: `initData → session JWT → PATCH /users/me` with the snake_case
 * `MeProfileUpdateRequest` body (identity comes from the JWT, never the body —
 * there is NO `telegram_user_id`). Coordinates are REQUIRED here: the server
 * columns are NOT-NULL strict floats, so if the onboarding form didn't carry a
 * picked city's lat/lon we throw rather than invent a birthplace.
 */
export async function createUser(
  body: UserCreateRequest,
  initData: string | null,
): Promise<MeProfileResponse> {
  userCreateRequestSchema.parse(body);
  if (MOCK_MODE) {
    return delay({
      user_id: `user_mock_${Date.now().toString(36)}`,
      onboarded: true,
      subscription_tier: "free",
      first_name: body.name,
    });
  }
  if (body.birthLat === undefined || body.birthLon === undefined) {
    throw new ApiError("Birth coordinates required — pick a city from the suggestions", 400, null);
  }
  if (!body.timezone) {
    throw new ApiError("Timezone required — pick a city from the suggestions", 400, null);
  }
  const token = await ensureSessionToken(initData);
  const patchBody: MeProfileUpdate = {
    first_name: body.name,
    birth_date: body.birthDate,
    birth_time: body.birthTime,
    birth_lat: body.birthLat,
    birth_lon: body.birthLon,
    timezone: body.timezone,
    birth_place: body.birthPlace,
  };
  return apiFetch({
    path: "/users/me",
    method: "PATCH",
    body: patchBody,
    token,
    schema: meProfileResponseSchema,
  });
}

export async function searchCities(query: string): Promise<readonly City[]> {
  if (MOCK_MODE) {
    const q = query.trim().toLowerCase();
    if (!q) return delay(MOCK_CITIES.slice(0, 5), 60);
    return delay(MOCK_CITIES.filter((c) => c.name.toLowerCase().startsWith(q)).slice(0, 8), 60);
  }
  // Atlas search is a PUBLIC route — no session needed. Map the server's
  // `{ results: [CityHit] }` envelope (timezone_iana, name_en, country_code)
  // onto the flat `City` the form consumes.
  const res = await apiFetch({
    path: `/atlas/search?q=${encodeURIComponent(query)}`,
    schema: atlasSearchSchema,
  });
  return res.results.map((hit, i) => ({
    id: `${hit.country_code}-${hit.lat.toFixed(3)},${hit.lon.toFixed(3)}-${i}`,
    name: hit.name,
    country: hit.country_code,
    timezone: hit.timezone_iana,
    lat: hit.lat,
    lon: hit.lon,
  }));
}

/* ----- Lunar snapshot for the Today card (delegates to the lunar client) ----- */

const PHASE_LABELS_TODAY: Readonly<Record<string, string>> = {
  new: "Новолуние",
  "waxing-crescent": "Растущий серп",
  "first-quarter": "Первая четверть",
  "waxing-gibbous": "Растущая выпуклая",
  full: "Полнолуние",
  "waning-gibbous": "Убывающая выпуклая",
  "last-quarter": "Последняя четверть",
  balsamic: "Бальзамическая луна",
};

const PHASE_TAGLINES_TODAY: Readonly<Record<string, string>> = {
  new: "Намерения, тишина, посев.",
  "waxing-crescent": "Импульс и первые шаги.",
  "first-quarter": "Преодоление сопротивления.",
  "waxing-gibbous": "Доработка и проверка деталей.",
  full: "Кульминация и проявление.",
  "waning-gibbous": "Распространение опыта.",
  "last-quarter": "Переосмысление итогов.",
  balsamic: "Отпускание и подготовка.",
};

const SIGN_LABELS_TODAY: Readonly<Record<string, string>> = {
  aries: "Овен",
  taurus: "Телец",
  gemini: "Близнецы",
  cancer: "Рак",
  leo: "Лев",
  virgo: "Дева",
  libra: "Весы",
  scorpio: "Скорпион",
  sagittarius: "Стрелец",
  capricorn: "Козерог",
  aquarius: "Водолей",
  pisces: "Рыбы",
};

const SIGN_GLYPHS_LOWER: Readonly<Record<string, string>> = {
  aries: "♈",
  taurus: "♉",
  gemini: "♊",
  cancer: "♋",
  leo: "♌",
  virgo: "♍",
  libra: "♎",
  scorpio: "♏",
  sagittarius: "♐",
  capricorn: "♑",
  aquarius: "♒",
  pisces: "♓",
};

/** Real `GET /lunar/today` snapshot → the Today card's `moonPhase` shape. */
async function fetchLunarTodaySnapshot(tz: string): Promise<TodayResponse["moonPhase"]> {
  const data = await fetchLunarToday(tz);
  const whole = Math.floor(data.sign_degree);
  const minutes = Math.round((data.sign_degree - whole) * 60);
  const glyph = SIGN_GLYPHS_LOWER[data.sign] ?? "";
  const signRu = SIGN_LABELS_TODAY[data.sign] ?? data.sign;
  const waxing = data.phase.is_waxing ? "растущая" : "убывающая";
  const label = PHASE_LABELS_TODAY[data.phase.name] ?? data.phase.name;
  const tagline = PHASE_TAGLINES_TODAY[data.phase.name] ?? "";
  const illumination = Math.round(data.phase.illumination_pct);
  const voc = data.void_of_course;
  const vocSentence =
    voc?.is_active && voc.next_sign
      ? ` Луна без курса до перехода в знак ${SIGN_LABELS_TODAY[voc.next_sign] ?? voc.next_sign} — отложите важные решения.`
      : "";
  return {
    label,
    position: `${whole}°${minutes.toString().padStart(2, "0")}′ ${glyph} · ${waxing}`.trim(),
    headline: `Луна в знаке ${signRu}.`,
    body: `${tagline} Освещённость диска — ${illumination}%.${vocSentence}`.trim(),
  };
}
